/**
 * Pure helpers for a person's access on record (5 Oct): the reference-user
 * panel in the access wizard and the IT Admin user lookup.
 *
 * WHY: both show the same "current picture" per system, e.g.
 * "CRM — Security role: Sales · Business unit: USL · Site: LaGrange (request
 * 10390)", and the wizard writes it into the new hire's request as a note
 * ("As reference user Anna Sørensen: …"). One formatter keeps them saying the
 * same thing. Pure (no React, no i18n): labels come in, so node tests run it.
 */

/** The answer shape these helpers read (the generated `ProfileAnswer`). */
export interface ProfileAnswerLike {
  key: string;
  values?: string[];
}

/** The request shape these helpers read (the generated `ProfileAccessRequest`). */
export interface ProfileRequestLike {
  number: string;
  answers?: ProfileAnswerLike[];
}

/** The system shape these helpers read (the generated `ProfileSystem`). */
export interface ProfileSystemLike {
  system: string;
  item_key?: string | null;
  current?: ProfileRequestLike | null;
  removed?: boolean;
}

/**
 * The answers worth copying to a new hire, in reading order. Type of access
 * and change type ("New", "Add") say how it was asked, not what it is, and
 * the system is the line's heading: left out.
 */
export const COPY_ANSWER_KEYS = [
  "security_role",
  "business_unit",
  "site",
  "currency",
  "roles",
  "companies",
] as const;

/** Every answer key, in the order the IT Admin profile lists them. */
export const ALL_ANSWER_KEYS = [
  ...COPY_ANSWER_KEYS,
  "type_of_access",
  "change_type",
  "effective_date",
] as const;

/** English labels, for the note the wizard writes into Ivanti (IT reads English). */
export const ENGLISH_ANSWER_LABELS: Record<string, string> = {
  security_role: "Security role",
  business_unit: "Business unit",
  site: "Site",
  currency: "Currency",
  roles: "Roles",
  companies: "Companies",
  type_of_access: "Type of access",
  change_type: "Change type",
  effective_date: "Effective date",
};

/** One answer's values, joined; multi-values with ", ", lines flattened. */
export function answerText(values: readonly string[] | undefined): string {
  return (values ?? [])
    .map((value) => value.replace(/\s*\n+\s*/g, " ").trim())
    .filter((value) => value !== "")
    .join(", ");
}

/**
 * "Security role: Sales · Business unit: USL · Site: LaGrange" — the
 * answers in `keys`, labelled, empty ones left out.
 */
export function profileSummary(
  request: ProfileRequestLike | null | undefined,
  labels: Record<string, string>,
  keys: readonly string[] = COPY_ANSWER_KEYS,
): string {
  if (request == null) {
    return "";
  }
  const byKey = new Map((request.answers ?? []).map((answer) => [answer.key, answer.values]));
  return keys
    .map((key) => {
      const text = answerText(byKey.get(key));
      return text === "" ? null : `${labels[key] ?? key}: ${text}`;
    })
    .filter((part): part is string => part !== null)
    .join(" · ");
}

/** "CRM — Security role: Sales · … (request 10390)"; `requestWord` is translated. */
export function profileLine(
  system: ProfileSystemLike,
  labels: Record<string, string>,
  requestWord: string,
): string {
  const summary = profileSummary(system.current, labels);
  const number = system.current?.number;
  const tail = number ? ` (${requestWord} ${number})` : "";
  return summary === "" ? `${system.system}${tail}` : `${system.system} — ${summary}${tail}`;
}

/** The longest note a checklist item takes (the API's `AccessNeed.note`). */
export const NOTE_MAX_LENGTH = 500;

/** The note written for the new hire: "As reference user <name>: <line>" (English, ≤ 500). */
export function referenceNote(personName: string, system: ProfileSystemLike): string {
  const note = `As reference user ${personName}: ${profileLine(system, ENGLISH_ANSWER_LABELS, "request")}`;
  return note.length <= NOTE_MAX_LENGTH ? note : `${note.slice(0, NOTE_MAX_LENGTH - 1)}…`;
}

/** Systems that can be copied: current, not removed, and a checklist item the wizard offers. */
export function copyableSystems<T extends ProfileSystemLike>(
  systems: readonly T[],
  itemKeys: readonly string[],
): T[] {
  return systems.filter(
    (system) =>
      system.current != null &&
      system.removed !== true &&
      system.item_key != null &&
      itemKeys.includes(system.item_key),
  );
}

/**
 * Copy one system to the new hire, or undo it: ticks the checklist item and
 * writes the reference note; unticking removes both, but keeps a note HR has
 * since edited (and the tick that goes with it).
 */
export function applyReferenceCopy(
  state: { ticked: string[]; notes: Record<string, string | undefined> },
  itemKey: string,
  note: string,
  copy: boolean,
): { ticked: string[]; notes: Record<string, string | undefined> } {
  if (copy) {
    const ticked = state.ticked.includes(itemKey) ? state.ticked : [...state.ticked, itemKey];
    const existing = (state.notes[itemKey] ?? "").trim();
    const merged = existing === "" || existing === note ? note : `${existing} / ${note}`;
    return { ticked, notes: { ...state.notes, [itemKey]: merged.slice(0, NOTE_MAX_LENGTH) } };
  }
  const existing = (state.notes[itemKey] ?? "").trim();
  if (existing !== note) {
    return state;
  }
  const notes = { ...state.notes };
  delete notes[itemKey];
  return { ticked: state.ticked.filter((key) => key !== itemKey), notes };
}

/** Whether a system is copied already: ticked, with the reference note in its note. */
export function isCopied(
  state: { ticked: string[]; notes: Record<string, string | undefined> },
  itemKey: string,
  note: string,
): boolean {
  return state.ticked.includes(itemKey) && (state.notes[itemKey] ?? "").includes(note);
}
